import { parseISO } from 'date-fns';
import { isPlainObject } from 'lodash';
import {
    type Activity,
    type Location,
    type Production,
    type Resource,
    type Schedule,
    type Team
} from 'sr-types/lib/production/v1/graphql';
import {
    type Order,
    type OrderOrganizationInfo,
    type Production as ProductionFromSearch,
    type ShortlistLineItem
} from 'sr-types/lib/search/v1/graphql';
import { getStringFromDateRange } from '../../production/helpers/activityUtils';
import { normalizeOrganizations } from '../../production/helpers/organizationUtils';
import { normalizePeople } from '../../production/helpers/peopleUtils';
import { PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import { formatRolesLabel } from '../keyCredits/roleFormatting';
import { toDisplayLocaleDateAndTime } from '../utils/dateTime';
import { REPORT_DATASOURCE_KEYS } from './constants';
import { keyValueArrayToObject, nameValueArrayToObject } from './reportUtils';
import { type ReportDataSourceKey } from './runtime/types/runtimeTypes';

type ContactType = ReturnType<typeof normalizePeople>[number];
type OrganizationType = ReturnType<typeof normalizeOrganizations>[number];

type ProfessionalRoles = ReadonlyArray<string | null | undefined> | null | undefined;

interface KeyCreditWithRoles extends Record<string, unknown> {
    professionalRoles?: ProfessionalRoles;
}

interface NormalizedKeyCredit extends KeyCreditWithRoles {
    professionalRolesLabel: string;
}

interface ShortlistLineItemWithKeyCredits extends ShortlistLineItem {
    keyCredits: unknown[];
}

export interface NormalizedShortlistLineItem extends ShortlistLineItem {
    keyCredits?: Array<NormalizedKeyCredit | unknown>;
}

export interface NormalizedProduction {
    id: string;
    name: string;
    otherTitles: Record<string, any>;
    projectStatus: string;
    productionStatus: string;
    about: string;
    currentStatusNote?: { content: string };
    genre: string[];
    keyPeople: ContactType[];
    projectOwnerContacts: ContactType[];
    keyActivities: string;
    keyOrganizations?: OrganizationType[];
    associatedOrgs?: OrganizationType[];
    productionType: string;
    productionSubType: string;
    attributes: Record<string, any>;
    showAttributes: Record<string, any>;
    createdByName?: string;
    createdOn: string;
    updatedOn: string;
    releaseDate: string;
    releaseYear: string;
    productionYears: string;
    productionAttributes?: string;
    ownerOrganizationName: string;
    tags: string[];
    locations: Location[];
    group: string;
    // Backward-compatible aliases used by older document templates.
    type?: string;
}

export interface NormalizeProductionOptions {
    /** Whether alternative titles retain the historical AKA prefix in report data. */
    includeAkaPrefix?: boolean;
}

export interface NormalizedResource {
    id: string;
    name: string;
    type: string;
    subType: string;
    role: string;
    status: string;
    summary: string;
    attributes: Record<string, any>;
    hiringTeam: Team;
    linkedActivities: Activity[];
    budgetCategory: string;
    resourceCategory: string;
    resourceSchedule: Schedule;
    createdByName?: string;
    createdOn: string;
    updatedOn: string;
    locations: Location[];
}

export interface NormalizedOrder {
    id: string;
    attributes: Record<string, any>;
    name: string;
    status: string;
    state: string;
    orderNumber: string;
    orderVersion: number;
    terms: string;
    salesOrganization: OrderOrganizationInfo;
    costSummary: Record<string, any>;
    internalNote?: Order['internalNote'];
    createdOn: string;
    updatedOn: string;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isProfessionalRoles(value: unknown): value is ProfessionalRoles {
    return (
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.every((role) => role === null || role === undefined || typeof role === 'string'))
    );
}

function hasArrayKeyCredits(value: unknown): value is ShortlistLineItemWithKeyCredits {
    return isObjectRecord(value) && Array.isArray(value.keyCredits);
}

function isShortlistLineItemArray(value: unknown): value is ShortlistLineItem[] {
    return Array.isArray(value);
}

export function formatLocationLabels(value: unknown): string {
    if (!Array.isArray(value)) {
        return PUNCTUATION_MARKS_CONSTANTS.dash;
    }

    const labels = value
        .map((item) => {
            if (!isObjectRecord(item)) {
                return '';
            }

            const label = item.label;
            return typeof label === 'string' ? label.trim() : '';
        })
        .filter(Boolean);

    return labels.length > 0 ? labels.join(', ') : PUNCTUATION_MARKS_CONSTANTS.dash;
}

export function normalizeProduction(
    p: Production,
    locale: string,
    { includeAkaPrefix = true }: NormalizeProductionOptions = {}
): NormalizedProduction {
    const locations = p.locations || [];
    const normalizedProduction: NormalizedProduction = {
        id: p.identity.id,
        name: (p.name || p.reference?.label) ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        otherTitles: keyValueArrayToObject(p.otherTitles, { includeAkaPrefix }),
        projectStatus: p.projectStatus ?? '',
        productionStatus: p.productionStatus ?? '',
        about: p.about?.trim() ? p.about : PUNCTUATION_MARKS_CONSTANTS.dash,
        genre: p.genre || [],
        keyPeople: normalizePeople(p.keyPeople),
        projectOwnerContacts: normalizePeople(p.projectOwnerContacts),
        keyActivities:
            Array.isArray(p.keyActivities) && p.keyActivities.length > 0
                ? p.keyActivities
                      .map((activity) => {
                          const dateRange = getStringFromDateRange(activity.dateRange, locale);
                          return `${activity.name}: ${dateRange}`;
                      })
                      .join(PUNCTUATION_MARKS_CONSTANTS.newLine)
                : PUNCTUATION_MARKS_CONSTANTS.dash,
        keyOrganizations: normalizeOrganizations(p.keyOrganizations),
        associatedOrgs: normalizeOrganizations(p.associatedOrgs),
        productionType: p.productionType?.trim() ? p.productionType : PUNCTUATION_MARKS_CONSTANTS.dash,
        productionSubType: p.productionSubType?.trim() ? p.productionSubType : PUNCTUATION_MARKS_CONSTANTS.dash,
        showAttributes: keyValueArrayToObject(p.showAttributes),
        attributes: nameValueArrayToObject(p.attributes),
        createdOn: p.createdOn
            ? toDisplayLocaleDateAndTime(parseISO(p.createdOn), true, locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        updatedOn: p.updatedOn
            ? toDisplayLocaleDateAndTime(parseISO(p.updatedOn), true, locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        releaseDate: p.releaseDate
            ? toDisplayLocaleDateAndTime(parseISO(p.releaseDate), false, locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        releaseYear: p.releaseYear ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        productionYears: p.productionYears?.trim() ? p.productionYears : PUNCTUATION_MARKS_CONSTANTS.dash,
        ownerOrganizationName: p.ownerOrganizationName ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        tags: p.tags || [],
        locations,
        type: p.productionType?.trim() ? p.productionType : PUNCTUATION_MARKS_CONSTANTS.dash,
        group: p.group ?? PUNCTUATION_MARKS_CONSTANTS.dash
    };

    return normalizedProduction;
}

export function normalizeSearchProductions(
    productions: ProductionFromSearch[],
    locale: string
): NormalizedProduction[] {
    return productions.map((p) => {
        const normalizedProduction: NormalizedProduction = {
            id: p.id,
            name: p.name ?? PUNCTUATION_MARKS_CONSTANTS.dash,
            otherTitles: keyValueArrayToObject(p.otherTitles),
            projectStatus: p.projectStatus ?? '',
            productionStatus: p.productionStatus ?? '',
            about: p.about?.trim() ? p.about : PUNCTUATION_MARKS_CONSTANTS.dash,
            currentStatusNote: {
                content: p.currentStatusNote?.content ?? PUNCTUATION_MARKS_CONSTANTS.dash
            },
            genre: p.genre || [],
            keyPeople: normalizePeople(p.keyPeople),
            projectOwnerContacts: normalizePeople(p.projectOwnerContacts),
            keyActivities:
                Array.isArray(p.keyActivities) && p.keyActivities.length > 0
                    ? p.keyActivities
                          .map((activity) => {
                              const dateRange = getStringFromDateRange(activity.dateRange, locale);
                              return `${activity.name}: ${dateRange}`;
                          })
                          .join(PUNCTUATION_MARKS_CONSTANTS.newLine)
                    : PUNCTUATION_MARKS_CONSTANTS.dash,
            keyOrganizations: normalizeOrganizations(p.keyOrganizations),
            productionType: p.productionType?.trim() ? p.productionType : PUNCTUATION_MARKS_CONSTANTS.dash,
            productionSubType: p.productionSubType?.trim() ? p.productionSubType : PUNCTUATION_MARKS_CONSTANTS.dash,
            showAttributes: keyValueArrayToObject(p.showAttributes),
            attributes: nameValueArrayToObject(p.attributes),
            createdByName: p.createdByName ?? PUNCTUATION_MARKS_CONSTANTS.dash,
            createdOn: p.createdOn
                ? toDisplayLocaleDateAndTime(parseISO(p.createdOn), true, locale)
                : PUNCTUATION_MARKS_CONSTANTS.dash,
            updatedOn: p.updatedOn
                ? toDisplayLocaleDateAndTime(parseISO(p.updatedOn), true, locale)
                : PUNCTUATION_MARKS_CONSTANTS.dash,
            releaseDate: p.releaseDate
                ? toDisplayLocaleDateAndTime(parseISO(p.releaseDate), false, locale)
                : PUNCTUATION_MARKS_CONSTANTS.dash,
            releaseYear: p.releaseYear ?? PUNCTUATION_MARKS_CONSTANTS.dash,
            productionYears: p.productionYears?.trim() ? p.productionYears : PUNCTUATION_MARKS_CONSTANTS.dash,
            productionAttributes: p.productionAttributes?.trim()
                ? p.productionAttributes
                : PUNCTUATION_MARKS_CONSTANTS.dash,
            ownerOrganizationName: p.ownerOrganizationName ?? PUNCTUATION_MARKS_CONSTANTS.dash,
            tags: p.tags || [],
            locations: p.locations || [],
            group: p.group ?? PUNCTUATION_MARKS_CONSTANTS.dash
        };

        return normalizedProduction;
    });
}

export function normalizeResource(r: Resource, _locale: string): NormalizedResource {
    const normalizedResource: NormalizedResource = {
        id: r.identity.id,
        name: r.name ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        type: r.type ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        subType: r.subType ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        role: r.role ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        status: r.status ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        summary: r.summary?.about,
        attributes: nameValueArrayToObject(r.attributes),
        hiringTeam: r.hiringTeam,
        budgetCategory: r.budgetCategory ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        resourceCategory: r.resourceCategory,
        resourceSchedule: r.resourceSchedule,
        linkedActivities: r.linkedActivities,
        createdByName: r.createdBy,
        createdOn: r.createdOn
            ? toDisplayLocaleDateAndTime(parseISO(r.createdOn), true, _locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        updatedOn: r.updatedOn
            ? toDisplayLocaleDateAndTime(parseISO(r.updatedOn), true, _locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        locations: r.locations || []
    };

    return normalizedResource;
}

export function normalizeShortlistLineItem(sli: ShortlistLineItem, _locale: string): NormalizedShortlistLineItem {
    if (!hasArrayKeyCredits(sli)) {
        return sli;
    }

    const normalizedKeyCredits = sli.keyCredits.map((credit): NormalizedKeyCredit | unknown => {
        if (!isObjectRecord(credit)) {
            return credit;
        }

        const professionalRoles = isProfessionalRoles(credit.professionalRoles) ? credit.professionalRoles : undefined;

        return {
            ...credit,
            professionalRolesLabel: formatRolesLabel(professionalRoles)
        };
    });

    return {
        ...sli,
        keyCredits: normalizedKeyCredits
    };
}

export function normalizeShortlistLineItems(
    shortlistLineItems: ShortlistLineItem[],
    locale: string
): NormalizedShortlistLineItem[] {
    return shortlistLineItems.map((sli) => {
        return normalizeShortlistLineItem(sli, locale);
    });
}

export function normalizeOrder(o: Order, locale: string): NormalizedOrder {
    const attributes = nameValueArrayToObject(o.attributes);
    const normalizedAttributes =
        isObjectRecord(attributes) && Array.isArray(attributes.locations)
            ? {
                  ...attributes,
                  locations: formatLocationLabels(attributes.locations)
              }
            : attributes;

    const normalizedOrder: NormalizedOrder = {
        id: o.id,
        attributes: normalizedAttributes,
        name: o.name ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        status: o.status ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        state: o.state ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        orderNumber: o.orderNumber ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        orderVersion: o.orderVersion,
        terms: o.terms ?? PUNCTUATION_MARKS_CONSTANTS.dash,
        salesOrganization: o.salesOrganization,
        costSummary: o.costSummary ?? {},
        internalNote: o.internalNote,
        createdOn: o.createdOn
            ? toDisplayLocaleDateAndTime(parseISO(o.createdOn), true, locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        updatedOn: o.updatedOn
            ? toDisplayLocaleDateAndTime(parseISO(o.updatedOn), true, locale)
            : PUNCTUATION_MARKS_CONSTANTS.dash
    };

    return normalizedOrder;
}

export function normalizeReportDataObject(
    row: Partial<Record<ReportDataSourceKey, unknown>>,
    locale: string
): Partial<Record<ReportDataSourceKey, unknown>> {
    const normalizedRow: Partial<Record<ReportDataSourceKey, unknown>> = {};

    (Object.keys(row) as ReportDataSourceKey[]).forEach((dataSourceKey) => {
        const value = row[dataSourceKey];

        if (value === undefined) {
            return;
        }

        if (dataSourceKey === REPORT_DATASOURCE_KEYS.production || dataSourceKey === REPORT_DATASOURCE_KEYS.workspace) {
            if (isPlainObject(value)) {
                normalizedRow[dataSourceKey] = normalizeProduction(value as Production, locale);
                return;
            }

            normalizedRow[dataSourceKey] = value;
            return;
        }

        if (dataSourceKey === REPORT_DATASOURCE_KEYS.resource) {
            if (isPlainObject(value)) {
                normalizedRow[dataSourceKey] = normalizeResource(value as Resource, locale);
                return;
            }

            normalizedRow[dataSourceKey] = value;
            return;
        }

        if (dataSourceKey === REPORT_DATASOURCE_KEYS.shortlistLineItems) {
            if (isShortlistLineItemArray(value)) {
                normalizedRow[dataSourceKey] = normalizeShortlistLineItems(value, locale);
                return;
            }

            if (isPlainObject(value) && isObjectRecord(value) && isShortlistLineItemArray(value.items)) {
                normalizedRow[dataSourceKey] = normalizeShortlistLineItems(value.items, locale);
                return;
            }

            normalizedRow[dataSourceKey] = value;
            return;
        }
    });

    return normalizedRow;
}
